import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { atiradorService, type ListResponse } from '../services/atiradores.service'
import { addToSyncQueue, cacheApiResponse, getCachedApiResponse } from '../../../lib/offlineDb'
import type { Atirador, AtiradorFilters } from '../types'

export const ATIRADORES_KEY = 'atiradores'

function isOfflineError(err: unknown): boolean {
  if (!navigator.onLine) return true
  if (typeof err === 'object' && err !== null) {
    const e = err as Record<string, unknown>
    if (e.message === 'Network Error') return true
    if (typeof e.message === 'string' && e.message.includes('Network')) return true
  }
  return false
}

export function useAtiradores(filters: AtiradorFilters = {}) {
  return useQuery<ListResponse<Atirador>>({
    queryKey: [ATIRADORES_KEY, filters],
    queryFn: async () => {
      const cacheKey = `atiradores:${JSON.stringify(filters)}`
      try {
        const data = await atiradorService.list(filters)
        await cacheApiResponse(cacheKey, data)
        return data
      } catch (err) {
        if (isOfflineError(err)) {
          const cached = await getCachedApiResponse(cacheKey)
          if (cached) return cached as ListResponse<Atirador>
        }
        throw err
      }
    },
  })
}

export function useAtirador(id: number | null) {
  return useQuery({
    queryKey: [ATIRADORES_KEY, id],
    queryFn: () => atiradorService.getById(id!),
    enabled: id !== null,
  })
}

export function useCreateAtirador() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (data: Omit<Atirador, 'idatirador' | 'data_cadastro' | 'clube_nome'>) => {
      try {
        return await atiradorService.create(data)
      } catch (err) {
        if (isOfflineError(err)) {
          await addToSyncQueue({
            table: 'atiradores',
            action: 'create',
            payload: data as unknown as Record<string, unknown>,
            endpoint: '/api/v1/atiradores',
            method: 'POST',
          })
          return { queued: true, syncPending: true } as unknown as Atirador
        }
        throw err
      }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: [ATIRADORES_KEY] }),
  })
}

export function useUpdateAtirador() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: Partial<Omit<Atirador, 'idatirador' | 'data_cadastro' | 'clube_nome'>> }) => {
      try {
        return await atiradorService.update(id, data)
      } catch (err) {
        if (isOfflineError(err)) {
          await addToSyncQueue({
            table: 'atiradores',
            action: 'update',
            payload: { id, ...data } as unknown as Record<string, unknown>,
            endpoint: `/api/v1/atiradores/${id}`,
            method: 'PUT',
          })
          return { queued: true, syncPending: true } as unknown as Atirador
        }
        throw err
      }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: [ATIRADORES_KEY] }),
  })
}

export function useEnviarConvite() {
  return useMutation({
    mutationFn: atiradorService.enviarConvite,
  })
}

export function useDeleteAtirador() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: number) => {
      try {
        return await atiradorService.delete(id)
      } catch (err) {
        if (isOfflineError(err)) {
          await addToSyncQueue({
            table: 'atiradores',
            action: 'delete',
            payload: { id },
            endpoint: `/api/v1/atiradores/${id}`,
            method: 'DELETE',
          })
          return { queued: true, syncPending: true }
        }
        throw err
      }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: [ATIRADORES_KEY] }),
  })
}
